import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { LeadService } from '../services/lead.service';
import { SnackBarService } from '../services/snack-bar.service';
import { environment } from '../../environments/environment';

interface DebtInfo {
  bankName: string;
  balance: number;
}

interface MortgageInfo {
  type: string;
  value: number | string | null;
}

interface BusinessInfo {
  name: string;
  hst: string;
  articleOfIncorporation: string;
  debt: number;
}

@Component({
  selector: 'app-firstmeeting-create',
  templateUrl: './firstmeeting-create.component.html',
  styleUrls: ['./firstmeeting-create.component.css']
})
export class FirstmeetingCreateComponent implements OnInit {
  form: FormGroup;
  debtList: DebtInfo[] = [];
  mortgageList: MortgageInfo[] = [];
  isSubmitting = false;
  customerId: string = '';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private leadService: LeadService,
    private snackBarService: SnackBarService
  ) {
    this.form = this.fb.group({
      clientName: ['', [Validators.required, Validators.minLength(2)]],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
      emailId: ['', [Validators.required, Validators.email]],
      debtorIncome: [''],
      spouseIncome: [''],
      childTax: [''],
      rentalIncome: [''],
      familySupport: [''],
      otherIncome: [''],
      propertyValue: [''],
      lopNeeded: ['no'],
      vehicleModel: [''],
      vehicleYear: [''],
      vehicleFinance: [''],
      businessName: [''],
      businessHst: [''],
      businessArticle: [''],
      businessDebt: [''],
      note: [''],
      terms: ['']
    });
  }

  ngOnInit(): void {
    // Get customer ID from query params
    this.route.queryParams.subscribe(params => {
      const customerId = params['customerId'];
      if (customerId) {
        // Store for later use in API call
        this.customerId = customerId;
      }
    });
  }

  addDebt(): void {
    this.debtList.push({ bankName: '', balance: null as any });
  }

  removeDebt(index: number): void {
    this.debtList.splice(index, 1);
  }

  addMortgage(): void {
    this.mortgageList.push({ type: 'mortgage', value: null as any });
  }

  removeMortgage(index: number): void {
    this.mortgageList.splice(index, 1);
  }

  getMortgageLabel(index: number): string {
    const ordinals: string[] = [
      "First", "Second", "Third", "Fourth", "Fifth",
      "Sixth", "Seventh", "Eighth", "Ninth", "Tenth",
      "Eleventh", "Twelfth", "Thirteenth", "Fourteenth", "Fifteenth",
      "Sixteenth", "Seventeenth", "Eighteenth", "Nineteenth"
    ];

    const tens: string[] = [
      "", "", "Twentieth", "Thirtieth", "Fortieth",
      "Fiftieth", "Sixtieth", "Seventieth", "Eightieth", "Ninetieth"
    ];

    const ones: string[] = [
      "", "First", "Second", "Third", "Fourth", "Fifth",
      "Sixth", "Seventh", "Eighth", "Ninth"
    ];

    const num = index + 1;

    if (num <= 19) {
      return `${ordinals[num - 1]} Mortgage`;
    }

    if (num < 100) {
      const ten = Math.floor(num / 10);
      const one = num % 10;

      if (one === 0) {
        return `${tens[ten]} Mortgage`;
      }

      return `${tens[ten].replace("ieth", "y")} ${ones[one]} Mortgage`;
    }

    if (num === 100) {
      return `Hundredth Mortgage`;
    }

    return `Mortgage ${num}`;
  }

  onSubmit(): void {
    // Validate mandatory fields
    const clientName = this.form.get('clientName')?.value?.trim();
    const phoneNumber = this.form.get('phoneNumber')?.value?.trim();
    const email = this.form.get('emailId')?.value?.trim();

    if (!clientName || !phoneNumber || !email) {
      this.snackBarService.openSnackBar('Please fill in all mandatory fields: Name, Phone, and Email', 'error-class');
      return;
    }

    if (!this.form.valid) {
      this.snackBarService.openSnackBar('Please fix the errors in the form', 'error-class');
      return;
    }

    // Build payload matching the API structure
    const payload = {
      customer_id: parseInt(this.customerId),
      client_name: clientName,
      phone_number: phoneNumber,
      email: email,
      debtors_income: this.form.get('debtorIncome')?.value || '',
      spouse_income: this.form.get('spouseIncome')?.value || '',
      child_tax: this.form.get('childTax')?.value || '',
      rental_income: this.form.get('rentalIncome')?.value || '',
      family_support: this.form.get('familySupport')?.value || '',
      any_other_income: this.form.get('otherIncome')?.value || '',
      property_value: this.form.get('propertyValue')?.value || '',
      lop_needed: this.form.get('lopNeeded')?.value === 'yes' ? 1 : 0,
      vehicle_model: this.form.get('vehicleModel')?.value || '',
      vehicle_year: this.form.get('vehicleYear')?.value || '',
      vehicle_finance_amount: this.form.get('vehicleFinance')?.value || '',
      bsns_name: this.form.get('businessName')?.value || '',
      bsns_hst: this.form.get('businessHst')?.value || '',
      bsns_article_ins: this.form.get('businessArticle')?.value || '',
      bsns_deal: this.form.get('businessDebt')?.value || '',
      notes: this.form.get('note')?.value || '',
      terms: this.form.get('terms')?.value || '',
      assets_info: this.mortgageList.map(m => m.value ? { mortgage: m.value } : null).filter(m => m !== null),
      debt_info: this.debtList.map(d => ({ name: d.bankName, balance: d.balance }))
    };

    this.isSubmitting = true;
    const apiUrl = `${environment.BASE_URL}/first-meeting/save/${this.customerId}`;
    
    this.http.post<any>(apiUrl, payload).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.snackBarService.openSnackBar('First meeting created successfully', 'success-class');
        this.router.navigate(['/dashboard/customer-list']);
      },
      error: (error) => {
        this.isSubmitting = false;
        console.error('Error creating first meeting:', error);
        this.snackBarService.openSnackBar(
          error.error?.message || 'Error creating first meeting',
          'error-class'
        );
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/dashboard/customer-list']);
  }

  validateAllSections(): boolean {
    return true;
  }

  getPhoneNumberError(): string {
    const control = this.form.get('phoneNumber');
    if (control?.hasError('required')) {
      return 'Phone number is required';
    }
    if (control?.hasError('pattern')) {
      return 'Phone number must be 10 digits';
    }
    return '';
  }

  getEmailError(): string {
    const control = this.form.get('emailId');
    if (control?.hasError('required')) {
      return 'Email is required';
    }
    if (control?.hasError('email')) {
      return 'Invalid email format';
    }
    return '';
  }
}
